// Which server this particular build chose, read once.
//
// Split from server.ts for the reason xp/app/src/server/thisBuild.ts is split
// from its own table: that file is imported by vite.config.ts, which runs in
// Node, where `import.meta.env` does not exist. Everything that touches it is
// here, and this module holds no rules of its own.
//
// Both values are inlined by Vite, so this is a constant by the time it reaches
// a browser and there is nothing a person at the console can do to change it.
// That is the intended cost: a console aimed at a different server is a
// different build, deployed to a different host.

import {
  consoleMark,
  consoleMarkParts,
  readServer,
  serverSentence,
  type ConsoleMarkParts,
  type ServerReading,
} from "./server.js";

/**
 * The server this build reads from, or the reason it has none.
 *
 * vite.config.ts refuses a build that does not resolve, so in a deployed bundle
 * this is expected to be `ok`. It is still a reading rather than a value,
 * because a bundle can be produced by something other than that config —
 * esbuild in check.mjs does exactly that — and an application that assumed the
 * build tool had already checked would render a login form aimed at nowhere.
 */
export const THIS_BUILD: ServerReading = readServer(
  import.meta.env["VITE_XP_ENV"],
  import.meta.env["VITE_NOVA_BASE_URL"],
);

/** Where requests go. Empty only where the build named no server, which App refuses. */
export const BASE_URL: string = THIS_BUILD.ok ? THIS_BUILD.baseUrl : "";

/**
 * Where a screen fetches a picture or a film. Empty only where the build
 * named no server. After Phase 4 this is the public API and BASE_URL is
 * admin; a preview that prefixes BASE_URL is a 404 on a route admin never
 * registered.
 */
export const API_URL: string = THIS_BUILD.ok ? THIS_BUILD.apiUrl : "";

/**
 * Where the public registration page lives for this build, or null.
 *
 * Null when the build named no server, and null when the named environment
 * carries no registration host (production). A QR is drawn only of this value.
 */
export const WEB_ORIGIN: string | null = THIS_BUILD.ok ? THIS_BUILD.webOrigin : null;

/**
 * Where the kiosk player lives for this build, or null.
 *
 * Null when the build named no server, and null when the named environment
 * carries no player host (production). A screen address is formed only of
 * this value.
 */
export const KIOSK_ORIGIN: string | null = THIS_BUILD.ok ? THIS_BUILD.kioskOrigin : null;

/** What the permanent mark says, or null on the live server. See consoleMark. */
export const CONSOLE_MARK: string | null = consoleMark(THIS_BUILD);

/** The same mark in the three pieces the strip sets it in. See consoleMarkParts. */
export const CONSOLE_MARK_PARTS: ConsoleMarkParts | null = consoleMarkParts(THIS_BUILD);

/** The same fact in a sentence, for the header and the refusal screen. */
export const SERVER_SENTENCE: string = serverSentence(THIS_BUILD);
